/**
 * Reference profile built from tests flagged `storeAsReference` (hardware values of
 * good, known riders). Candidates are later scored against these statistics.
 */


import { analyzeTest } from "./evaluator";
import { DEFAULT_SEQUENCE } from "./turns";
const ENV = {}; // tunables (same defaults as the Cloud Functions)

const MIN_REFERENCE_RUNS = Number(ENV.MIN_REFERENCE_RUNS || 3);
const TOLERANCE = 1.25;
// Minimum headroom above the reference maximum, so a handful of reference runs never yields a zero-width limit
const LIMIT_FLOOR = {
  durationSec: 3, jerkRms: 0.05, peakAccelG: 0.05, harshCount: 2, rollRms: 3,
  pitchRms: 3, yawRms: 5, vibrationG: 0.01, turnCount: 0, avgTurnDurationMs: 300,
};
const FEATURE_KEYS = [
  "durationSec", "jerkRms", "peakAccelG", "harshCount",
  "rollRms", "pitchRms", "yawRms", "vibrationG", "turnCount", "avgTurnDurationMs",
];

const mean = (xs) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0);
const std = (xs) => {
  const m = mean(xs);
  return xs.length ? Math.sqrt(mean(xs.map((x) => (x - m) ** 2))) : 0;
};
const round = (v, d = 3) => Math.round(v * 10 ** d) / 10 ** d;

function stats(values, key) {
  const xs = values.filter((v) => Number.isFinite(v));
  if (!xs.length) return null;
  const m = mean(xs);
  const s = std(xs);
  const max = Math.max(...xs);
  return {
    mean: round(m),
    std: round(s),
    min: round(Math.min(...xs)),
    max: round(max),
    limit: round(Math.max(max * TOLERANCE, m + 2 * s, max + (LIMIT_FLOOR[key] ?? 1e-3))),
    n: xs.length,
  };
}

/** tests: [{ testId, test }] raw RTDB nodes. */
function buildReferenceProfile(tests) {
  if (tests.length < MIN_REFERENCE_RUNS) {
    throw new Error(`Need at least ${MIN_REFERENCE_RUNS} reference tests, found ${tests.length}`);
  }

  const runs = tests.map(({ testId, test }) => {
    const a = analyzeTest(test, null);
    if (!a.features) throw new Error(`Not enough sensor samples in ${testId}`);
    return { testId, features: a.features, turns: a.turnAnalysis.turns };
  });

  const features = {};
  FEATURE_KEYS.forEach((k) => {
    const s = stats(runs.map((r) => r.features[k]), k);
    if (s) features[k] = s;
  });

  // Expected turn sequence = most common sequence among references (fallback R,L,R,L)
  const counts = {};
  runs.forEach((r) => {
    const key = r.turns.map((t) => t.direction).join(",");
    counts[key] = (counts[key] || 0) + 1;
  });
  const [bestSeq, bestCount] = Object.entries(counts).sort((a, b) => b[1] - a[1])[0] || ["", 0];
  const learned = bestSeq && bestCount >= Math.ceil(runs.length / 2) ? bestSeq.split(",") : null;
  const expectedSequence = learned || DEFAULT_SEQUENCE;

  const byDirection = {};
  ["RIGHT", "LEFT"].forEach((dir) => {
    const t = runs.flatMap((r) => r.turns).filter((x) => x.direction === dir);
    if (!t.length) return;
    byDirection[dir] = {
      samples: t.length,
      avgPeakRate: round(mean(t.map((x) => x.peakRate))),
      maxPeakRate: round(Math.max(...t.map((x) => x.peakRate)) * TOLERANCE),
      avgDurationMs: Math.round(mean(t.map((x) => x.durationMs))),
      maxDurationMs: Math.round(Math.max(...t.map((x) => x.durationMs)) * TOLERANCE),
      minDurationMs: Math.round(Math.min(...t.map((x) => x.durationMs)) * 0.5),
      avgAngleDeg: t[0].angleDeg === null ? null : round(mean(t.map((x) => x.angleDeg || 0)), 1),
    };
  });

  return {
    version: 2,
    referenceRuns: runs.length,
    testIds: runs.map((r) => r.testId),
    expectedSequence,
    sequenceLearned: !!learned,
    expectedTurns: expectedSequence.length,
    features,
    turns: { byDirection },
    perTest: runs.map((r) => ({ testId: r.testId, ...r.features, sequence: r.turns.map((t) => t.direction) })),
    createdAt: new Date().toISOString(),
  };
}

export { buildReferenceProfile, MIN_REFERENCE_RUNS, FEATURE_KEYS };
